import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChartDataService } from '../../services/ChartDataService';
import { useUnifiedAuth } from '../../auth/useUnifiedAuth';
import { useSAPDataStore } from '../../store/SAPDataStore';
import { useChartCache } from '../../hooks/useChartCache';
import { useThemedCharts } from '../../hooks/useThemedCharts';
import BaseChart from './BaseChart';

interface DowntimeAccumulated {
  int: Record<string, number>;
  ext: Record<string, number>;
}

interface DowntimeChartProps {
  machineSerialNo?: string;
  lineId?: string; // Line ID for line-level aggregated data
  shifts: string[];
  syncGroup?: string;
}

const DowntimeChart: React.FC<DowntimeChartProps> = ({ machineSerialNo, lineId: _lineId, shifts }) => {
  const { t } = useTranslation();
  const { token } = useUnifiedAuth();
  const { dateRange, getMachineById, getTargetForMachine } = useSAPDataStore();
  const { getStatusColors } = useThemedCharts();
  const [data, setData] = useState<DowntimeAccumulated | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use chart cache for better performance
  const chartCache = useChartCache<DowntimeAccumulated>({
    maxAge: 5 * 60 * 1000, // 5 minutes
    maxEntries: 30 // Cache up to 30 different chart configurations
  });

  const fetchDowntimeData = async () => {
    // Don't fetch if no shifts selected
    if (!shifts || shifts.length === 0) {
      setData(null);
      setLoading(false);
      return;
    }

    if (!machineSerialNo || !dateRange.start || !dateRange.end) return;
    if (!token) {
      setError('Authentication required');
      return;
    }

    // Check cache first
    const cachedData = chartCache.getCachedData(
      'pc.downtime',
      machineSerialNo,
      dateRange,
      shifts
    );

    if (cachedData) {
      setData(cachedData);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = ChartDataService.buildRequestParams(machineSerialNo, dateRange, shifts);
      const response = await ChartDataService.fetchDowntime(params, token);

      console.log('📊 DowntimeChart: Raw API response:', {
        success: response.success,
        error: response.error,
        dataType: typeof response.data,
        data: response.data
      });

      if (response.success && response.data) {
        const responseData = response.data as any;
        // Backend returns { status, results, error } - extract results
        const results = responseData.results || responseData;
        let downtimeData = results.downtime_accumulated || results;
        
        // Fallback: if data is grouped under an "undefined" key (backend bug),
        // unwrap it and treat the array as ungrouped fault list
        if (downtimeData && typeof downtimeData === 'object' && 'undefined' in downtimeData && !downtimeData.int && !downtimeData.ext) {
          const raw = downtimeData['undefined'];
          if (Array.isArray(raw)) {
            // Treat all items as internal faults (no int/ext distinction available)
            downtimeData = { int: raw, ext: [] };
          }
        }
        
        console.log('📊 DowntimeChart: Extracted data:', {
          responseKeys: Object.keys(responseData),
          resultsKeys: results ? Object.keys(results) : 'null',
          downtimeDataKeys: downtimeData ? Object.keys(downtimeData) : 'null'
        });
        
        setData(downtimeData);
        chartCache.setCachedData('pc.downtime', machineSerialNo, dateRange, shifts, downtimeData);
      } else {
        setError(response.error || 'Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching downtime data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch downtime data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDowntimeData();
  }, [machineSerialNo, dateRange.start, dateRange.end, shifts]);

  // Transform data for BaseChart
  // API format: { int: [{ name, value }], ext: [{ name, value }] }
  const chartData = React.useMemo(() => {
    if (!data) return { categories: [], series: [], intMap: {} as Record<string, number>, extMap: {} as Record<string, number> };

    console.log('📊 DowntimeChart chartData: Input data:', {
      hasInt: !!data.int,
      hasExt: !!data.ext,
      intIsArray: Array.isArray(data.int),
      extIsArray: Array.isArray(data.ext)
    });

    // Handle array format: [{ name, value }, ...]
    // Convert to map for easier lookup
    const intMap: Record<string, number> = {};
    const extMap: Record<string, number> = {};
    const allFaults = new Set<string>();

    if (Array.isArray(data.int)) {
      data.int.forEach((item: { name: string; value: number }) => {
        const name = item.name || 'Unknown';
        const value = typeof item.value === 'string' ? parseFloat(item.value) : (item.value || 0);
        intMap[name] = value;
        allFaults.add(name);
      });
    } else if (data.int && typeof data.int === 'object') {
      // Legacy object format
      Object.entries(data.int).forEach(([name, value]) => {
        intMap[name] = typeof value === 'string' ? parseFloat(value) : (value as number || 0);
        allFaults.add(name);
      });
    }

    if (Array.isArray(data.ext)) {
      data.ext.forEach((item: { name: string; value: number }) => {
        const name = item.name || 'Unknown';
        const value = typeof item.value === 'string' ? parseFloat(item.value) : (item.value || 0);
        extMap[name] = value;
        allFaults.add(name);
      });
    } else if (data.ext && typeof data.ext === 'object') {
      // Legacy object format
      Object.entries(data.ext).forEach(([name, value]) => {
        extMap[name] = typeof value === 'string' ? parseFloat(value) : (value as number || 0);
        allFaults.add(name);
      });
    }

    // Sort faults by total downtime (descending)
    const faultArray = Array.from(allFaults).sort((a, b) => {
      const totalA = (intMap[a] || 0) + (extMap[a] || 0);
      const totalB = (intMap[b] || 0) + (extMap[b] || 0);
      return totalB - totalA;
    });

    console.log('📊 DowntimeChart chartData: Processed:', {
      faultCount: faultArray.length,
      sampleFaults: faultArray.slice(0, 3)
    });
    
    return {
      categories: faultArray,
      intMap,
      extMap,
      series: [
        {
          name: 'Internal Faults',
          data: faultArray.map(fault => {
            const value = intMap[fault] || 0;
            return value / (1000 * 60 * 60); // Convert ms to hours
          })
        },
        {
          name: 'External Faults',
          data: faultArray.map(fault => {
            const value = extMap[fault] || 0;
            return value / (1000 * 60 * 60); // Convert ms to hours
          })
        }
      ]
    };
  }, [data]);

  // Calculate dynamic target for the current machine
  const dynamicTarget = React.useMemo(() => {
    if (!machineSerialNo) return null;
    
    const machine = getMachineById(machineSerialNo);
    if (!machine) return null;
    
    // Get maxDowntime target (in hours)
    return getTargetForMachine(machine, 'maxDowntime');
  }, [machineSerialNo, getMachineById, getTargetForMachine]);

  // Prepare plot lines with dynamic target
  const plotLines = React.useMemo(() => {
    const lines = [];
    
    if (dynamicTarget !== null) {
      lines.push({
        value: dynamicTarget,
        color: getStatusColors.plotLine,
        dashStyle: 'Dash' as const,
        width: 2,
        label: {
          text: 'Max Downtime Target',
          style: {
            color: getStatusColors.plotLine
          }
        }
      });
    }
    
    return lines;
  }, [dynamicTarget, getStatusColors.plotLine]);

  const customOptions = React.useMemo(() => {
    return {
      yAxis: {
        opposite: true, // This moves the Y-axis (values) to the top for horizontal bar charts
        title: {
          text: 'Downtime Duration (Hours)',
          style: {
            fontSize: '14px',
            fontWeight: '500',
            color: getStatusColors.yAxisTitle
          }
        },
        plotLines: plotLines,
        labels: {
          formatter: function(this: any) {
            return this.value.toFixed(1) + 'h';
          },
          style: {
            fontSize: '12px',
            color: getStatusColors.yAxisLabels
          }
        }
      },
      // Override series with Highcharts point objects containing originalMs
      series: chartData.series.map((s, seriesIndex) => ({
      name: s.name,
      type: 'bar' as const,
      data: chartData.categories.map((fault, pointIndex) => {
        // Use the maps from chartData instead of directly accessing data
        const originalMs = seriesIndex === 0 
          ? (chartData.intMap?.[fault] || 0) 
          : (chartData.extMap?.[fault] || 0);
        return {
          y: s.data[pointIndex], // Use the hours value for display
          originalMs: originalMs, // Store original milliseconds for tooltip
          category: fault
        };
      })
    })),
    tooltip: {
      formatter: function(this: any) {
        const point = this.point;
        const originalMs = point.originalMs || 0;
        
        // Format milliseconds to HH:MM:SS.mmm
        const hours = Math.floor(originalMs / (1000 * 60 * 60));
        const minutes = Math.floor((originalMs % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((originalMs % (1000 * 60)) / 1000);
        const ms = originalMs % 1000;
        
        const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
        
        return `<b>${this.series.name}</b><br/>${point.category}: ${formattedTime}`;
      }
    }
    };
  }, [chartData, plotLines, getStatusColors.yAxisTitle, getStatusColors.yAxisLabels]);

  // Show loading state in BaseChart when loading (check BEFORE shifts check)
  if (loading) {
    return (
      <div className="w-full">
        <BaseChart
          type="bar"
          height={400}
          categories={[]}
          series={[]}
          loading={true}
        />
      </div>
    );
  }

  if (!shifts || shifts.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📊</div>
          <h3 className="text-lg font-semibold theme-text-primary mb-2">{t('charts.noData')}</h3>
          <p className="theme-text-secondary">No shifts selected or available for the selected timeframe</p>
        </div>
      </div>
    );
  }

  // Check for data
  const hasDowntimeData = data && (Object.keys(data.int || {}).length > 0 || Object.keys(data.ext || {}).length > 0);
  
  if (!hasDowntimeData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📊</div>
          <h3 className="text-lg font-semibold theme-text-primary mb-2">{t('charts.noDowntimeData')}</h3>
          <p className="theme-text-secondary">No downtime events found for the selected period.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <BaseChart
        commentProps={{ chartType: 'downtime', machineBK: machineSerialNo }}
        type="bar"
        height={Math.max(400, chartData.categories.length * 40 + 100)}
        categories={chartData.categories}
        series={chartData.series}
        yAxis={{
          title: 'Downtime Duration (Hours)',
          min: 0,
          format: '{value:.1f}h'
        }}
        customOptions={customOptions}
        legend={{
          enabled: true,
          position: 'bottom'
        }}
        loading={loading}
        error={error}
        className="w-full"
      />
    </div>
  );
};

export default DowntimeChart;
